import { StateCreator } from 'zustand';
import { nanoid } from 'nanoid';

export type NotificationType = 'success' | 'error' | 'info' | 'warning';

export type Notification = {
	id: number | string;
	type: NotificationType;
	title: string;
	body: string;
	created_at: string;
	readed: boolean;

	url?: string;
	actionLabel?: string;
	onClick?: () => void;

	source: 'backend' | 'frontend';

	modalData?: {
		imageSrc: string;
		title: string;
		description: string;
		buttonAction?: {
			text: string;
			onClick: () => void;
		};
		linkAction?: {
			text: string;
			href: string;
		};
	};
};

export type NotificationState = {
	notifications: Record<number | string, Notification>;
	addNotification: (newNotification: Notification) => void;
	setNotifications: (notifications: Notification[]) => void;
	markAsRead: (notificationId: number | string) => void;
	showNotification: (n: Omit<Notification, 'id' | 'created_at' | 'readed' | 'source'>) => void;
	clearOne: (notificationId: string | number) => void;
	clearAllRead: () => void;

	clearAll: () => void;
	resetNotificationSlice: () => void;
};

const initialNotificationState: Pick<NotificationState, 'notifications'> = {
	notifications: {},
};

export const createNotificationSlice: StateCreator<NotificationState> = (set) => ({
	...initialNotificationState,

	addNotification: (newNotification) =>
		set((state) => ({
			notifications: { [newNotification.id]: newNotification, ...state.notifications },
		})),

	setNotifications: (notifications) =>
		set(() => ({
			notifications: Object.fromEntries(notifications.map((n) => [n.id, n])),
		})),

	markAsRead: (notificationId) =>
		set((state) => {
			if (!state.notifications[notificationId]) return state;
			return {
				notifications: {
					...state.notifications,
					[notificationId]: { ...state.notifications[notificationId], readed: true },
				},
			};
		}),

	clearAll: () => set(() => ({ notifications: {} })),

	clearOne: (notificationId: string | number) =>
		set((state) => {
			const newNotifications = { ...state.notifications };
			delete newNotifications[notificationId];
			return { notifications: newNotifications };
		}),

	clearAllRead: () =>
		set((state) => ({
			notifications: Object.fromEntries(
				Object.entries(state.notifications).filter(([_, notif]) => !notif.readed),
			),
		})),

	showNotification: (notificationInput) => {
		const id = nanoid();
		const created_at = new Date().toISOString();

		const newNotification: Notification = {
			...notificationInput,
			id,
			created_at,
			readed: false,
			source: 'frontend',
		};

		set((state) => ({
			notifications: {
				[id]: newNotification,
				...state.notifications,
			},
		}));
	},
	resetNotificationSlice: () => set(() => initialNotificationState),
});
